import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useRef, useState, type FormEvent } from 'react';
import { api, type Me } from '../api';
import { cents, money, moneyInput } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';

type View = 'out' | 'in' | 'review' | 'done' | 'all' | 'unpaid';
type Category = keyof typeof CATEGORIES;
interface Candidate { id: number; direction: 'outgoing' | 'incoming'; series: string; number: string; issueDate: string; party: string; currency: string; owed: number; amount: number; paidByHand?: boolean }
interface Suggestion extends Candidate { byNumber: boolean; byAmount: boolean; byName: boolean; certain: boolean }
interface Allocation { id: number; invoiceId: number; amount: number; auto: boolean; series: string; number: string; direction: 'outgoing' | 'incoming'; party: string }
interface Transaction {
  id: number; accountId: number | null; bookingDate: string; amount: number; currency: string; counterparty: string; counterpartyIban: string; purpose: string; reference: string;
  status: 'new' | 'matched' | 'explained'; category: Category | null; note: string; auto: boolean; left: number; allocations: Allocation[]; suggestions: Suggestion[]; hint: Category | null;
}
interface Account { id: number; iban: string; currency: string; label: string; active: boolean; bank: string; coveredUntil: string | null; missing: string[]; balance: { amount: number; date: string } | null; empty?: boolean }
interface Statement { id: number; accountId: number | null; fileName: string; format: string; iban: string; periodFrom: string | null; periodTo: string | null; entries: number; added: number; balanceOk: boolean | null; importedAt: string }
interface Bank { accounts: Account[]; statements: Statement[]; transactions: Transaction[]; counts: Record<Exclude<View, 'unpaid'>, number>; candidates: { in: Candidate[]; out: Candidate[] } }
type Open = Candidate & { paid: number; dueDate: string | null; daysOverdue: number };
interface Unpaid { receivable: Open[]; payable: Open[] }
interface UploadResult { format: string; linked: number; explained: number; corrected?: { from: string; to: string }[]; statements: { iban: string; bank: string; added: number; entries: number; balanceOk: boolean | null }[] }

/** Reasons for a payment without an invoice, and the direction each fits. */
const CATEGORIES = {
  cash_withdrawal: ['out', 'Grynųjų išėmimas į kasą'],
  cash_deposit: ['in', 'Grynųjų įnešimas iš kasos'],
  owner_loan: ['in', 'Savininko paskola įmonei'],
  owner_loan_repay: ['out', 'Paskolos grąžinimas savininkui'],
  accountable: ['out', 'Avansas atskaitingam asmeniui'],
  member_loan: ['out', 'Paskola nariui (savininkui) — grąžintina'],
  member_loan_repay: ['in', 'Nario grąžinta paskola'],
  salary: ['out', 'Darbo užmokestis'],
  tax_vmi: ['any', 'Mokesčiai VMI'],
  tax_profit: ['any', 'Pelno mokestis (VMI)'],
  tax_sodra: ['any', 'Sodros įmokos'],
  member_psd: ['out', 'MB nario PSD įmoka (Sodra, kodas 322)'],
  bank_fee: ['out', 'Banko mokestis'],
  own_transfer: ['any', 'Pervedimas tarp savų sąskaitų'],
  related_transfer: ['any', 'Pervedimas susijusiai įmonei'],
  provider_payout: ['in', 'Mokėjimų sistemos išmoka (Stripe, Paysera)'],
  dividends: ['out', 'Dividendai'],
  capital: ['in', 'Įstatinis kapitalas'],
  loan: ['any', 'Paskola / lizingas'],
  refund: ['any', 'Grąžinimas'],
  card_purchase: ['out', 'Pirkimas kortele (laukiama dokumento)'],
  for_accountant: ['any', 'Perduota buhalteriui (sutvarkys buhalteris)'],
  accountant_done: ['any', 'Sutvarkė buhalteris'],
  other: ['any', 'Kita (su pastaba)'],
} as const;
const WAITS_FOR_DOCUMENT: readonly string[] = ['card_purchase', 'accountable', 'for_accountant'];

/**
 * Where to get a statement: each bank's own internet bank (opened in a new tab). The statement is downloaded
 * there (XML / ISO 20022 or CSV; a PDF only with its balances) and uploaded here.
 */
const BANK_SITES: [string, string][] = [
  ['Swedbank', 'https://www.swedbank.lt/business'], ['SEB', 'https://www.seb.lt'], ['Luminor', 'https://www.luminor.lt'],
  ['Artea (Šiaulių bankas)', 'https://www.artea.lt'], ['Citadele', 'https://www.citadele.lt'], ['Urbo bankas', 'https://www.urbo.lt'],
  ['Revolut', 'https://business.revolut.com'], ['Paysera', 'https://bank.paysera.com'],
];
const bankSite = (bank: string) => BANK_SITES.find(([name]) => name === bank)?.[1];

/** "Get a statement from your bank": the internet banks, and how. */
function BankLinks() {
  const { t } = useI18n();
  return (
    <details className="bank-links">
      <summary className="button button--ghost">{t('Išrašas iš banko')} ↗</summary>
      <div className="bank-links__panel">
        <p className="muted">{t('Prisijunkite prie savo banko, atsisiųskite sąskaitos išrašą XML (ISO 20022) arba CSV formatu ir įkelkite jį čia. Tą patį išrašą galite įkelti kelis kartus: jau įkelti mokėjimai nedubliuojami. PDF tinka tik tada, kai jame yra pradžios ir pabaigos likutis.')}</p>
        <ul>{BANK_SITES.map(([name, url]) => <li key={name}><a href={url} target="_blank" rel="noopener noreferrer">{name} ↗</a></li>)}</ul>
      </div>
    </details>
  );
}

const UPLOAD_ERRORS: Record<string, string> = {
  statement_unknown_format: 'Šio failo formato nepavyko atpažinti. Įkelkite XML, CSV, Excel arba PDF išrašą.',
  statement_invalid: 'Išrašo nepavyko perskaityti.',
  statement_no_account: 'Faile nėra sąskaitos numerio (IBAN). Įrašykite jį ir įkelkite dar kartą.',
  statement_old_excel: 'Senas Excel (.xls) failas: išsaugokite jį kaip .xlsx arba CSV.',
  statement_ai_limit: 'Šį mėnesį PDF išrašų nuskaitymo riba pasiekta. Įkelkite XML arba CSV.',
  statement_reader_unavailable: 'PDF skaitytuvas šiuo metu nepasiekiamas. Bandykite vėliau arba įkelkite XML / CSV.',
  statement_too_long: 'Išrašas per ilgas PDF skaitymui. Įkelkite XML arba CSV, arba trumpesnį laikotarpį.',
  statement_empty: 'Išraše nerasta mokėjimų.',
  statement_balance_mismatch: 'PDF išrašas perskaitytas netiksliai: pradžios likutis ir mokėjimai nesudaro pabaigos likučio. Niekas neįrašyta. Atsisiųskite išrašą iš banko XML arba CSV formatu ir įkelkite jį.',
  statement_unverifiable: 'Šiame PDF išraše nėra pradžios ir pabaigos likučio, todėl negalima patikrinti, ar jis perskaitytas teisingai. Niekas neįrašyta. Atsisiųskite išrašą iš banko XML arba CSV formatu ir įkelkite jį.',
  statement_signs_suspect: 'PDF išraše kai kurie jau įkelti mokėjimai perskaityti priešinga kryptimi (įplaukos kaip išlaidos). Niekas neįrašyta. Atsisiųskite išrašą iš banko XML arba CSV formatu ir įkelkite jį.',
  iban_invalid: 'Neteisingas IBAN.',
  file_too_large: 'Failas per didelis.',
};
const FORMATS: Record<string, string> = { camt: 'XML', mt940: 'MT940', csv: 'CSV', xlsx: 'Excel', ai: 'PDF' };
const label = (c: { series: string | null; number: string | null }) => `${c.series ?? ''} ${c.number ?? ''}`.trim();
const spaced = (iban: string) => iban.replace(/(.{4})/g, '$1 ').trim();

/** Bankas — statements of every account; the engine links payments to invoices; what is left waits here. */
export function BankPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [view, setView] = useState<View>('out');
  const [account, setAccount] = useState<number | null>(null);
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const [pending, setPending] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const listView = view === 'unpaid' ? 'out' : view;
  const bank = useQuery({ queryKey: ['bank', listView, account], queryFn: () => api.get<Bank>(`api/v1/bank?view=${listView}${account ? `&account=${account}` : ''}`) });
  const unpaid = useQuery({ queryKey: ['bank-unpaid'], queryFn: () => api.get<Unpaid>('api/v1/bank/unpaid'), enabled: view === 'unpaid' });
  const refresh = () => Promise.all(['bank', 'bank-unpaid', 'invoices', 'dashboard', 'receivables', 'cash'].map((key) => queryClient.invalidateQueries({ queryKey: [key] })));
  const act = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onSuccess: refresh });

  const upload = async (file: File, iban = '') => {
    setNotice(null);
    setBusy(true);
    const body = new FormData();
    if (iban) body.append('iban', iban);
    body.append('file', file);
    try {
      const r = await fetch('api/v1/bank/statements', { method: 'POST', body, credentials: 'same-origin', headers: { 'x-fsp-request': '1' } });
      const data = await r.json().catch(() => null) as (UploadResult & { error?: { code?: string; iban?: string } }) | null;
      if (r.ok && data?.statements) {
        setPending(null);
        const added = data.statements.reduce((s, x) => s + x.added, 0);
        const entries = data.statements.reduce((s, x) => s + x.entries, 0);
        const wrong = data.statements.some((s) => s.balanceOk === false);
        const parts = entries
          ? [t('Įkelta naujų mokėjimų: {added} iš {entries}.', { added, entries }), t('Susieta automatiškai: {n}.', { n: data.linked }), t('Paaiškinta automatiškai: {n}.', { n: data.explained })]
          : [];
        // A statement without payments still registers its account (and covers its period).
        for (const st of data.statements.filter((x) => !x.entries)) parts.push(t('Sąskaita {iban} ({bank}) pridėta: operacijų nėra.', { iban: spaced(st.iban), bank: st.bank }));
        for (const c of data.corrected ?? []) parts.unshift(t('Išraše perskaitytas IBAN {from} neteisingas – pataisytas į {to}.', { from: spaced(c.from), to: spaced(c.to) }));
        if (wrong) parts.push(t('Dėmesio: likučiai nesutampa su mokėjimų suma — patikrinkite išrašą.'));
        setNotice({ kind: wrong ? 'error' : 'success', text: parts.join(' ') });
      } else {
        const code = data?.error?.code ?? '';
        if (code === 'statement_no_account' || code === 'statement_iban_invalid') setPending(file);
        setNotice({ kind: 'error', text: code === 'statement_iban_invalid'
          ? t('Išraše perskaitytas IBAN {iban} neteisingas (gali būti klaida skaitant PDF). Įrašykite teisingą sąskaitos numerį ir įkelkite dar kartą.', { iban: spaced(data?.error?.iban ?? '') })
          : t(UPLOAD_ERRORS[code] ?? 'Nepavyko įkelti.') });
      }
    } finally {
      setBusy(false);
      await refresh();
    }
  };
  const reconcileNow = async () => {
    const r = await api.post<{ linked: number; explained: number }>('api/v1/bank/reconcile');
    setNotice({ kind: 'success', text: `${t('Susieta automatiškai: {n}.', { n: r.linked })} ${t('Paaiškinta automatiškai: {n}.', { n: r.explained })}` });
    await refresh();
  };

  const b = bank.data;
  const tabs: [View, string, number | undefined][] = [
    ['out', 'Trūksta dokumento', b?.counts.out], ['in', 'Nežinomos įplaukos', b?.counts.in], ['review', 'Patikrinti', b?.counts.review],
    ['done', 'Sutvarkyta', b?.counts.done], ['all', 'Visi', b?.counts.all], ['unpaid', 'Neapmokėtos sąskaitos', undefined],
  ];
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Mokėjimai')} title={t('Bankas')} text={t('Įkelkite visų sąskaitų išrašus (XML, CSV, Excel ar PDF). Mokėjimai susiejami su sąskaitomis automatiškai; kas lieka — sąrašuose žemiau.')}
      action={<>
        <BankLinks />
        <button type="button" className="button button--ghost" disabled={busy} onClick={() => void reconcileNow()}>{t('Suderinti dabar')}</button>
        <button type="button" className="button button--primary" disabled={busy} onClick={() => input.current?.click()}>{busy ? t('Skaitoma…') : t('Įkelti išrašą')}</button>
        <input ref={input} type="file" accept=".xml,.csv,.txt,.sta,.940,.mt940,.xlsx,.pdf,.jpg,.jpeg,.png" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ''; }} />
      </>} />}>
      {notice ? <Alert kind={notice.kind}>{notice.text}</Alert> : null}
      {pending ? <IbanForm file={pending} onSubmit={(iban) => void upload(pending, iban)} onCancel={() => setPending(null)} /> : null}
      {act.isError ? <Alert kind="error">{errorText(t, act.error)}</Alert> : null}

      {b?.accounts.length ? <Accounts accounts={b.accounts} selected={account} onSelect={setAccount} act={act.mutate} /> : null}

      <nav className="project-tabs page-tabs">
        {tabs.map(([key, name, count]) => (
          <button key={key} type="button" className={view === key ? 'active' : ''} onClick={() => setView(key)}>{t(name)}{count !== undefined ? ` (${count})` : ''}</button>
        ))}
      </nav>

      {view === 'unpaid' ? <UnpaidLists data={unpaid.data} /> : (
        <section className="panel-card">
          {b && !b.transactions.length ? (
            <div className="empty-state compact">
              <h3>{b.statements.length ? t('Šiame sąraše nieko nėra') : t('Mokėjimų nėra')}</h3>
              {!b.statements.length ? <p>{t('Internetinėje bankininkystėje atsisiųskite sąskaitos išrašą (XML, ISO 20022 camt.053, arba CSV; tinka ir Excel, o PDF tik su pradžios ir pabaigos likučiu) ir įkelkite jį čia. Galite įkelti kelių bankų ir kelių sąskaitų išrašus.')}</p> : null}
            </div>
          ) : null}
          <div className="bank-list">
            {b?.transactions.map((tx) => <Row key={tx.id} tx={tx} candidates={tx.amount > 0 ? b.candidates.in : b.candidates.out} act={act.mutate} />)}
          </div>
        </section>
      )}

      {b?.statements.length ? (
        <section className="panel-card">
          <h2>{t('Įkelti išrašai')}</h2>
          <div className="bank-statements">
            {b.statements.map((s) => (
              <div key={s.id} className="drive-row">
                <span className="drive-open"><span className="drive-icon">{FORMATS[s.format] ?? s.format}</span> {s.fileName}</span>
                <small>
                  {spaced(s.iban)} · {s.periodFrom ?? '…'} – {s.periodTo ?? '…'} · {t('nauji {added} iš {entries}', { added: s.added, entries: s.entries })}
                  {s.balanceOk === true ? ` · ✓ ${t('likučiai sutampa')}` : s.balanceOk === false ? ` · ⚠ ${t('likučiai nesutampa')}` : ''}
                </small>
                <span className="drive-actions">
                  <button type="button" className="delete-x" aria-label={t('Ištrinti')} title={t('Ištrinti')} onClick={() => { if (confirm(t('Ištrinti išrašą „{name}“ ir jo mokėjimus? Jų susiejimai su sąskaitomis bus atšaukti.', { name: s.fileName }))) act.mutate(() => api.delete(`api/v1/bank/statements/${s.id}`)); }}>×</button>
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </PanelShell>
  );
}

function IbanForm({ file, onSubmit, onCancel }: { file: File; onSubmit: (iban: string) => void; onCancel: () => void }) {
  const { t } = useI18n();
  const [iban, setIban] = useState('');
  const submit = (e: FormEvent) => { e.preventDefault(); onSubmit(iban); };
  return (
    <form className="panel-card bank-form bank-iban" onSubmit={submit}>
      <label><span>{t('Sąskaitos IBAN failui „{name}“', { name: file.name })}</span><input value={iban} onChange={(e) => setIban(e.target.value)} placeholder="LT00 0000 0000 0000 0000" required /></label>
      <button type="submit" className="button button--primary button--small">{t('Įkelti')}</button>
      <button type="button" className="text-link" onClick={onCancel}>{t('Atšaukti')}</button>
    </form>
  );
}

function Accounts({ accounts, selected, onSelect, act }: { accounts: Account[]; selected: number | null; onSelect: (id: number | null) => void; act: (fn: () => Promise<unknown>) => void }) {
  const { t } = useI18n();
  const [showClosed, setShowClosed] = useState(false);
  const shown = accounts.filter((a) => a.active || showClosed);
  const closed = accounts.filter((a) => !a.active).length;
  // Money on the accounts in use (euro accounts with a known balance), as the statements show it.
  const known = accounts.filter((a) => a.active && a.currency === 'EUR' && a.balance);
  const total = known.reduce((sum, a) => sum + a.balance!.amount, 0);
  return (
    <section className="bank-accounts">
      {known.length > 1 ? <p className="bank-total">{t('Sąskaitose iš viso')}: <strong>{money(total)}</strong> <small>{t('pagal išrašus')}</small></p> : null}
      {/* One row whatever the number of accounts: they share the width, and scroll sideways when there are many. */}
      <div className="bank-accounts__row">
        {shown.map((a) => <AccountCard key={a.id} a={a} selected={selected === a.id} onSelect={() => onSelect(selected === a.id ? null : a.id)} act={act} />)}
      </div>
      {closed ? <button type="button" className="text-link" onClick={() => setShowClosed(!showClosed)}>{showClosed ? t('Slėpti nenaudojamas') : t('Rodyti nenaudojamas ({n})', { n: closed })}</button> : null}
    </section>
  );
}

/** One account: the bank, the name the company gives it (edited only with the pencil), the IBAN, coverage and gaps. */
function AccountCard({ a, selected, onSelect, act }: { a: Account; selected: boolean; onSelect: () => void; act: (fn: () => Promise<unknown>) => void }) {
  const { t } = useI18n();
  const nameDialog = useRef<HTMLDialogElement>(null);
  const closeDialog = useRef<HTMLDialogElement>(null);
  const [name, setName] = useState(a.label);
  const saveName = (e: FormEvent) => {
    e.preventDefault();
    act(() => api.patch(`api/v1/bank/accounts/${a.id}`, { label: name.trim() }));
    nameDialog.current?.close();
  };
  const openName = () => { setName(a.label); nameDialog.current?.showModal(); };
  return (
    <article className={`bank-account${selected ? ' is-selected' : ''}${a.active ? '' : ' is-closed'}${a.missing.length ? ' is-missing' : ''}`}>
      <button type="button" className="bank-account__main" title={t('Rodyti tik šios sąskaitos mokėjimus')} onClick={onSelect}><strong>{a.bank}</strong></button>
      <div className="bank-account__name">
        {a.label ? <button type="button" className="bank-account__label" title={t('Rodyti tik šios sąskaitos mokėjimus')} onClick={onSelect}>{a.label}</button> : <span className="muted">{t('Sąskaitos pavadinimas')}</span>}
        <button type="button" className="bank-account__edit" aria-label={t('Keisti pavadinimą')} title={t('Keisti pavadinimą')} onClick={openName}>✎</button>
      </div>
      <button type="button" className="bank-account__main" title={t('Rodyti tik šios sąskaitos mokėjimus')} onClick={onSelect}>
        <span>{spaced(a.iban)}{a.currency !== 'EUR' ? ` · ${a.currency}` : ''}</span>
        <small>{a.coveredUntil ? t('Išrašai iki {date}', { date: a.coveredUntil }) : t('Išrašų dar nėra')}</small>
      </button>
      {a.balance ? <p className={`bank-account__balance${a.balance.amount < 0 ? ' is-negative' : ''}`} title={t('Pagal išrašo likutį ir vėlesnius mokėjimus')}>
        {money(a.balance.amount, a.currency)} <small>{t('likutis {date}', { date: a.balance.date })}</small></p> : null}
      {bankSite(a.bank) ? <a className="bank-account__get" href={bankSite(a.bank)} target="_blank" rel="noopener noreferrer" title={t('Atsisiųskite išrašą banke ir įkelkite čia')}>{t('Gauti išrašą banke')} ↗</a> : null}
      {a.missing.length ? <p className="bank-account__missing">⚠ {t('Trūksta išrašų: {months}', { months: a.missing.join(', ') })}</p> : null}
      <button type="button" className="bank-account__close" onClick={() => (a.active ? closeDialog.current?.showModal() : act(() => api.patch(`api/v1/bank/accounts/${a.id}`, { active: true })))}>
        {a.active ? t('Nebenaudojama (uždaryta)') : t('Vėl naudojama')}
      </button>
      {a.empty ? <button type="button" className="bank-account__close" onClick={() => { if (confirm(t('Ištrinti sąskaitą {iban}? Joje nėra nei išrašų, nei mokėjimų.', { iban: spaced(a.iban) }))) act(() => api.delete(`api/v1/bank/accounts/${a.id}`)); }}>{t('Ištrinti sąskaitą')}</button> : null}

      <dialog ref={nameDialog} className="confirm-dialog">
        <form className="dialog-card" onSubmit={saveName}>
          <p className="eyebrow">{a.bank} · {spaced(a.iban)}</p>
          <h2>{t('Sąskaitos pavadinimas')}</h2>
          <label>{t('Pavadinimas (pvz. projektas ar paskirtis)')}<input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoFocus /></label>
          <div className="dialog-actions">
            <button type="button" className="button button--ghost" onClick={() => nameDialog.current?.close()}>{t('Atšaukti')}</button>
            <button type="submit" className="button button--primary">OK</button>
          </div>
        </form>
      </dialog>

      <dialog ref={closeDialog} className="confirm-dialog">
        <div className="dialog-card">
          <p className="eyebrow">{a.bank} · {spaced(a.iban)}</p>
          <h2>{t('Pažymėti sąskaitą kaip nenaudojamą?')}</h2>
          <p>{t('Sąskaita bus paslėpta, o trūkstamų išrašų įspėjimai nebebus rodomi. Jos mokėjimai ir išrašai liks; sąskaitą bet kada galėsite vėl įjungti.')}</p>
          <div className="dialog-actions">
            <button type="button" className="button button--ghost" onClick={() => closeDialog.current?.close()}>{t('Atšaukti')}</button>
            <button type="button" className="button button--danger" onClick={() => { closeDialog.current?.close(); act(() => api.patch(`api/v1/bank/accounts/${a.id}`, { active: false })); }}>{t('Nebenaudojama (uždaryta)')}</button>
          </div>
        </div>
      </dialog>
    </article>
  );
}

function Row({ tx, candidates, act }: { tx: Transaction; candidates: Candidate[]; act: (fn: () => Promise<unknown>) => void }) {
  const { t } = useI18n();
  const [mode, setMode] = useState<'' | 'link' | 'reason'>('');
  const dir = tx.amount > 0 ? 'in' : 'out';
  const open = tx.left > 0 && (tx.status === 'new' || (tx.status === 'explained' && WAITS_FOR_DOCUMENT.includes(tx.category ?? '')));
  const allocate = (invoiceId: number, amount?: number) => act(() => api.post(`api/v1/bank/transactions/${tx.id}/allocate`, { invoiceId, ...(amount ? { amount } : {}) }));
  const explain = (category: Category, note = '', always = false) => act(() => api.post(`api/v1/bank/transactions/${tx.id}/explain`, { category, note, always }));
  return (
    <div className={`bank-row is-${tx.status}`}>
      <span className="bank-date">{tx.bookingDate}</span>
      <div className="bank-what">
        <strong>{tx.counterparty || '—'}</strong>
        <small>{[tx.purpose, tx.reference].filter(Boolean).join(' · ') || spaced(tx.counterpartyIban)}</small>
      </div>
      <strong className={`bank-amount ${tx.amount > 0 ? 'is-in' : 'is-out'}`}>{tx.amount > 0 ? '+' : '−'}{money(Math.abs(tx.amount), tx.currency)}</strong>
      <div className="bank-actions">
        {tx.allocations.map((a) => (
          <span key={a.id} className={`bank-chip${a.auto ? ' is-auto' : ''}`}>
            <Link to={a.direction === 'incoming' ? '/incoming/$id' : '/outgoing/$id'} params={{ id: String(a.invoiceId) }}>{label(a)}</Link> {money(a.amount, tx.currency)}
            {a.auto ? <small> · {t('automatiškai')}</small> : null}
            <button type="button" aria-label={t('Atsieti')} title={t('Atsieti')} onClick={() => act(() => api.delete(`api/v1/bank/transactions/${tx.id}/allocations/${a.id}`))}>×</button>
          </span>
        ))}
        {tx.category ? (
          <span className={`bank-chip is-reason${tx.auto ? ' is-auto' : ''}`}>
            {t(CATEGORIES[tx.category]?.[1] ?? tx.category)}{tx.note ? ` — ${tx.note}` : ''}{tx.auto ? <small> · {t('automatiškai')}</small> : null}
            <button type="button" aria-label={t('Atšaukti')} title={t('Atšaukti')} onClick={() => act(() => api.post(`api/v1/bank/transactions/${tx.id}/unexplain`))}>×</button>
          </span>
        ) : null}
        {tx.left > 0 && tx.allocations.length ? <span className="bank-left">{t('Nesusieta: {amount}', { amount: money(tx.left, tx.currency) })}</span> : null}
        {tx.auto && tx.status !== 'new' ? <button type="button" className="button button--ghost button--small" onClick={() => act(() => api.post(`api/v1/bank/transactions/${tx.id}/confirm`))}>{t('Teisinga')}</button> : null}

        {open ? <>
          {tx.suggestions.map((s) => (
            <button key={s.id} type="button" className={`bank-suggestion${s.certain ? ' is-certain' : ''}`} onClick={() => allocate(s.id)}
              title={[s.byNumber ? t('numeris paskirtyje') : '', s.byAmount ? t('suma sutampa') : t('liko mokėti: {amount}', { amount: money(s.amount, s.currency) }), s.byName ? t('mokėtojas sutampa') : ''].filter(Boolean).join(', ')}>
              {t('Susieti su')} <strong>{label(s)}</strong> <small>{s.party} · {money(Math.min(s.amount, tx.left), s.currency)}{s.paidByHand ? ` · ${t('pažymėta apmokėta ranka')}` : ''}</small>
            </button>
          ))}
          {tx.hint && !tx.category ? (
            <button type="button" className="bank-suggestion is-reason" onClick={() => tx.hint && explain(tx.hint)}>{t('Tai')}: <strong>{t(CATEGORIES[tx.hint][1])}</strong></button>
          ) : null}
          <button type="button" className="text-link" onClick={() => setMode(mode === 'link' ? '' : 'link')}>{t('Susieti su sąskaita…')}</button>
          {!tx.category ? <button type="button" className="text-link" onClick={() => setMode(mode === 'reason' ? '' : 'reason')}>{t('Be sąskaitos — nurodyti priežastį…')}</button> : null}
          {dir === 'out' ? <Link to="/incoming/new" className="text-link">{t('Įkelti gautą sąskaitą')}</Link> : <Link to="/outgoing/new" className="text-link">{t('Išrašyti sąskaitą')}</Link>}
          {/* One click to the accountant's list (Buhalterija → Buhalteriui), also when a reason is already given (e.g. a card purchase). */}
          {tx.category !== 'for_accountant' ? <button type="button" className="text-link" title={t('Perduoti buhalteriui sutvarkyti')} onClick={() => explain('for_accountant')}>{t('Buhalteriui')}</button> : null}
        </> : null}
        {mode === 'link' && open ? <LinkForm tx={tx} candidates={candidates} onLink={(id, amount) => { allocate(id, amount); setMode(''); }} /> : null}
        {mode === 'reason' && open ? <ReasonForm dir={dir} hasParty={Boolean(tx.counterparty || tx.counterpartyIban)} onSave={(c, note, always) => { explain(c, note, always); setMode(''); }} /> : null}
      </div>
    </div>
  );
}

function LinkForm({ tx, candidates, onLink }: { tx: Transaction; candidates: Candidate[]; onLink: (invoiceId: number, amount?: number) => void }) {
  const { t } = useI18n();
  const usable = candidates.filter((c) => c.currency === tx.currency);
  const [invoiceId, setInvoiceId] = useState('');
  const [amount, setAmount] = useState('');
  const chosen = usable.find((c) => String(c.id) === invoiceId);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!chosen) return;
    const value = cents(amount);
    onLink(chosen.id, value > 0 ? value : undefined);
  };
  return (
    <form className="bank-form" onSubmit={submit}>
      <select value={invoiceId} aria-label={t('Sąskaita')} required
        onChange={(e) => { setInvoiceId(e.target.value); const c = usable.find((x) => String(x.id) === e.target.value); setAmount(c ? moneyInput(Math.min(c.amount, tx.left)) : ''); }}>
        <option value="">{t('Pasirinkite sąskaitą…')}</option>
        {usable.map((c) => <option key={c.id} value={c.id}>{label(c)} · {c.party} · {t('liko {amount}', { amount: money(c.amount, c.currency) })}{c.paidByHand ? ` · ${t('pažymėta apmokėta ranka')}` : ''}</option>)}
      </select>
      <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" aria-label={t('Suma')} placeholder={t('Suma')} />
      <button type="submit" className="button button--primary button--small" disabled={!chosen}>{t('Susieti')}</button>
      <small className="muted">{t('Mažesnė suma — dalinis apmokėjimas.')}</small>
    </form>
  );
}

function ReasonForm({ dir, hasParty, onSave }: { dir: 'in' | 'out'; hasParty: boolean; onSave: (category: Category, note: string, always: boolean) => void }) {
  const { t } = useI18n();
  const options = (Object.keys(CATEGORIES) as Category[]).filter((k) => CATEGORIES[k][0] === 'any' || CATEGORIES[k][0] === dir);
  const [category, setCategory] = useState<Category | ''>('');
  const [note, setNote] = useState('');
  const [always, setAlways] = useState(false);
  const submit = (e: FormEvent) => { e.preventDefault(); if (category) onSave(category, note, always); };
  return (
    <form className="bank-form" onSubmit={submit}>
      <select value={category} aria-label={t('Priežastis')} onChange={(e) => setCategory(e.target.value as Category)} required>
        <option value="">{t('Priežastis…')}</option>
        {options.map((k) => <option key={k} value={k}>{t(CATEGORIES[k][1])}</option>)}
      </select>
      <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder={category === 'other' ? t('Pastaba (būtina)') : t('Pastaba')} required={category === 'other'} />
      {hasParty ? <label className="recurring-check"><input type="checkbox" checked={always} onChange={(e) => setAlways(e.target.checked)} /> {t('Visada šiam gavėjui / mokėtojui')}</label> : null}
      <button type="submit" className="button button--primary button--small">{t('Išsaugoti')}</button>
    </form>
  );
}

function UnpaidLists({ data }: { data: Unpaid | undefined }) {
  const { t } = useI18n();
  if (!data) return null;
  const table = (rows: Open[], incoming: boolean) => rows.length ? (
    <div className="table-wrap">
      <table className="invoice-table">
        <thead><tr><th>{t('Sąskaita')}</th><th>{incoming ? t('Tiekėjas') : t('Pirkėjas')}</th><th>{t('Data')}</th><th>{t('Terminas')}</th><th className="num">{t('Suma')}</th><th className="num">{t('Apmokėta')}</th><th className="num">{t('Liko')}</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td><Link to={incoming ? '/incoming/$id' : '/outgoing/$id'} params={{ id: String(r.id) }}>{label(r)}</Link></td>
              <td>{r.party}</td><td>{r.issueDate}</td>
              <td>{r.dueDate ?? '—'}{r.daysOverdue > 0 ? <small className="bank-overdue"> · {t('vėluoja {n} d.', { n: r.daysOverdue })}</small> : null}</td>
              <td className="num">{money(r.owed, r.currency)}</td><td className="num">{r.paid ? money(r.paid, r.currency) : '—'}</td><td className="num"><strong>{money(r.amount, r.currency)}</strong></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : <p className="muted">{t('Nėra.')}</p>;
  const sum = (rows: Open[]) => rows.filter((r) => r.currency === 'EUR').reduce((s, r) => s + r.amount, 0);
  return (
    <>
      <section className="panel-card">
        <h2>{t('Mums skolingi (išrašytos, neapmokėtos)')} · {money(sum(data.receivable))}</h2>
        {table(data.receivable, false)}
      </section>
      <section className="panel-card">
        <h2>{t('Mes skolingi (gautos, neapmokėtos)')} · {money(sum(data.payable))}</h2>
        {table(data.payable, true)}
      </section>
    </>
  );
}
